import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@ncoder/ui';
import { Github, GitBranch, CheckCircle, RefreshCw, AlertCircle, ArrowRight } from 'lucide-react';
import { Label } from '@ncoder/ui';
import { Button } from '@ncoder/ui';

export interface RepositoryInfo {
  name: string;
  owner: string;
  branch: string;
  lastSync: string;
  status: 'synced' | 'syncing' | 'error';
  url: string;
}

interface RepositoryStatusCardProps {
  repository: RepositoryInfo;
  onOpenUrl?: () => void;
}

export const RepositoryStatusCard: React.FC<RepositoryStatusCardProps> = ({
  repository,
  onOpenUrl,
}) => {
  const getSyncStatusIcon = () => {
    switch (repository.status) {
      case 'synced':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'syncing':
        return <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Github className="h-5 w-5" />
            Repository Status
          </CardTitle>
          <div className="flex items-center gap-2">
            {getSyncStatusIcon()}
            <span className="text-sm text-muted-foreground">
              Last sync: {repository.lastSync}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-sm font-medium">Repository</Label>
            <p className="text-lg font-semibold">
              {repository.owner}/{repository.name}
            </p>
          </div>
          <div>
            <Label className="text-sm font-medium">Current Branch</Label>
            <div className="flex items-center gap-2">
              <p className="text-lg font-semibold">{repository.branch}</p>
              <GitBranch className="h-4 w-4 text-muted-foreground" />
            </div>
          </div>
          <div>
            <Label className="text-sm font-medium">Repository URL</Label>
            <div className="flex items-center gap-2">
              <Button
                variant="link"
                className="p-0 h-auto text-blue-600"
                onClick={onOpenUrl}
              >
                View on GitHub
              </Button>
              <ArrowRight className="h-4 w-4" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
